import React, { useState } from "react";
import { Control, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import InputBottomSheet from "../../../components/InputBottomSheet";
import { useCartData } from "../../../providers/cartDataStore";
import useGetReferralPointsQuery from "../../../providers/referrals/useGetReferralPointsQuery";
import { ReactComponent as PencilIcon } from "../../../assets/icon/pencil.svg";
import { CartFormValues } from "./types";
import { useAuth } from "../../../contexts/auth-context";
import { IonIcon, IonPopover, IonSkeletonText } from "@ionic/react";
import { informationCircleOutline, trashOutline } from "ionicons/icons";
import cln from "classnames";
import { useConfig } from "../../../providers/config";
import { useTranslation } from "react-i18next";

interface BonusPointsProps {
  control: Control<CartFormValues>;
}

const BonusPoints = ({ control }: BonusPointsProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const userId = user?.id;
  const [showInfoPopover, setShowInfoPopover] = useState(false);
  const config = useConfig();
  const isGetReferralPointsQueryEnabled = config.isReferralEnabled && !!userId;

  const { data: userRewardPoints = 0, isLoading } = useGetReferralPointsQuery({
    userId: userId!,
    options: {
      enabled: isGetReferralPointsQueryEnabled,
    },
  });
  const { setRewardPoints, rewardPoints, cartTotalValue, resetRewardPoints } = useCartData();

  const isDisabled = !userId || !userRewardPoints;

  const pointsText =
    userRewardPoints > 0
      ? `${t("Your bonus points")}: ${userRewardPoints - rewardPoints}`
      : userId
        ? t("You don't have enough bonus points!")
        : t("Log in to your account to use points!");

  const rewardPointsText = `${t("Selected quantity of points")}: ${rewardPoints}`;

  const handleResetRewardPoints = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    resetRewardPoints();
  };

  const renderBonusPointsInputPreview = () => {
    if (isGetReferralPointsQueryEnabled && isLoading) {
      return (
        <div className=" flex items-center justify-start h-[5.375rem] w-full p-2.5 py-4">
          <div className={cln("flex flex-col w-full", isDisabled && "opacity-60")}>
            <div className="flex justify-between items-center w-full gap-1 font-bold text-[#292929]">{t("Points")}</div>
            <IonSkeletonText className="w-40 text-sm rounded-lg" />
          </div>
          <IonSkeletonText className="w-4 h-4 rounded-full" />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between px-2.5 py-4 border-t min-h-[5.375rem]">
        <div className={cln("flex flex-col", isDisabled && "opacity-60")}>
          <div className="flex items-center gap-1 text-[#292929]">{t("Points")}</div>
          <div className="text-xs text-[#656565]">{pointsText}</div>
          {userId && userRewardPoints > 0 && rewardPoints > 0 && (
            <div className="text-sm text-[#292929]">{rewardPointsText}</div>
          )}
        </div>

        {userId && userRewardPoints ? (
          <div className="flex items-center gap-1">
            {userId && userRewardPoints > 0 && rewardPoints > 0 && (
              <div className="flex items-center gap-1" onClick={handleResetRewardPoints}>
                <IonIcon icon={trashOutline} className="text-sm font-bold w-4 h-4 text-red-500" />
              </div>
            )}
            <PencilIcon className="text-sm font-bold w-4 h-4 text-[#D5D5D5]" />
          </div>
        ) : (
          <div onClick={() => setShowInfoPopover(true)} className="h-6">
            <IonIcon icon={informationCircleOutline} color="medium" className=" cursor-pointer" id="module-info-icon" />
          </div>
        )}
        <IonPopover
          isOpen={showInfoPopover}
          onDidDismiss={() => setShowInfoPopover(false)}
          trigger="module-info-icon"
          side="bottom"
          alignment="center"
          arrow={true}
          dismissOnSelect={true}
          className="module-info-popover"
        >
          <div className="p-2 text-xs text-gray-700">
            {userId
              ? config.inviteFriendDesc
              : t("Log in to your account and earn points that can be used at checkout.")}
          </div>
        </IonPopover>
      </div>
    );
  };

  const handleUseRewardPoints = (points: string) => {
    const pointsNum = parseInt(points, 10);
    if (isNaN(pointsNum) || pointsNum < 0 || pointsNum > cartTotalValue) {
      return t("Incorrect input");
    }

    if (pointsNum + rewardPoints > (userRewardPoints ?? 0)) {
      return t("Not enough bonus points");
    }

    setRewardPoints(pointsNum);
    return null;
  };

  return (
    <Controller
      name="point"
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <InputBottomSheet
          value={value}
          title={t("Points")}
          disabledPreview={isDisabled}
          errorMessage={error?.message}
          inputPlaceholder={t("Enter points")}
          renderPreview={renderBonusPointsInputPreview}
          onFooterSaveClickChange={(val) => {
            const errorMessage = handleUseRewardPoints(val);
            if (typeof errorMessage === "string") {
              toast.error(errorMessage);
            } else {
              onChange(val);
            }
          }}
        />
      )}
    />
  );
};

export default BonusPoints;
